import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, Sparkles, RefreshCw } from "lucide-react";
import HelpBubble from "../components/HelpBubble";

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function Insights() {
  const [data, setData] = useState(null);
  const [bench, setBench] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [{ data: ins }, { data: b }] = await Promise.all([
        api.get("/ai/insights"),
        api.get("/benchmark/verticals"),
      ]);
      setData(ins);
      setBench(b.verticals || []);
    } catch {
      toast.error("Insights failed");
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []);

  const verdictColor = (v) => ({
    scale: "text-emerald-600 bg-emerald-500/10 border-emerald-500/20",
    watch: "text-amber-600 bg-amber-500/10 border-amber-500/20",
    kill: "text-red-600 bg-red-500/10 border-red-500/20",
  }[v] || "text-slate-500 bg-zinc-800 border-zinc-700");

  return (
    <div className="p-6 max-w-[1600px]" data-testid="insights-page" style={{ fontFamily: "Manrope" }}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">AI Insights <HelpBubble feature="insights_summary" /></h1>
          <p className="text-xs text-slate-500 mt-1 font-mono">Winners, losers, and where to scale · powered by Claude Sonnet 4.5</p>
        </div>
        <button onClick={load} data-testid="refresh-insights" className="flex items-center gap-2 bg-white border border-slate-200 hover:bg-slate-100 rounded-md px-3 py-1.5 text-sm">
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> {busy ? "Thinking…" : "Re-run"}
        </button>
      </div>

      {!data && (
        <div className="rounded-md border border-slate-200 bg-white p-12 text-center text-slate-500 text-sm">
          {busy ? "Analysing campaigns…" : "No insights yet."}
        </div>
      )}

      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Narrative */}
          <div className="lg:col-span-3 bg-white border border-slate-200 rounded-md p-5">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500 font-mono mb-2">
              <Sparkles className="w-3 h-3 text-blue-600" /> Daily Brief
            </div>
            <p className="text-sm leading-relaxed text-slate-700" data-testid="insights-summary">{data.summary}</p>
          </div>

          {/* Winners */}
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-emerald-600 font-mono mb-3">
              <TrendingUp className="w-3 h-3" /> Winners
            </div>
            <div className="space-y-2" data-testid="winners-list">
              {(data.winners || []).map((w) => (
                <div key={w.name || w.vertical} className="bg-white border border-slate-200 rounded p-2.5">
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-xs text-slate-900 truncate">{w.name}</div>
                    <div className="text-xs font-mono text-emerald-600">ROI {Number(w.roi).toFixed(0)}%</div>
                  </div>
                  <div className="text-[11px] text-slate-500 font-mono flex gap-2">
                    <span>{w.vertical}</span>
                    <span>·</span>
                    <span className="text-emerald-600">+{fmtMoney(w.net_profit)}</span>
                  </div>
                  {w.reason && <div className="text-[11px] text-slate-500 mt-1">{w.reason}</div>}
                </div>
              ))}
            </div>
          </div>

          {/* Losers */}
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-red-600 font-mono mb-3">
              <TrendingDown className="w-3 h-3" /> Losers
            </div>
            <div className="space-y-2" data-testid="losers-list">
              {(data.losers || []).map((w) => (
                <div key={w.name || w.vertical} className="bg-white border border-slate-200 rounded p-2.5">
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-xs text-slate-900 truncate">{w.name}</div>
                    <div className="text-xs font-mono text-red-600">ROI {Number(w.roi).toFixed(0)}%</div>
                  </div>
                  <div className="text-[11px] text-slate-500 font-mono flex gap-2">
                    <span>{w.vertical}</span>
                    <span>·</span>
                    <span className="text-red-600">{fmtMoney(w.net_profit)}</span>
                  </div>
                  {w.reason && <div className="text-[11px] text-slate-500 mt-1">{w.reason}</div>}
                </div>
              ))}
            </div>
          </div>

          {/* Heatmap */}
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono mb-3">Vertical Verdict</div>
            <div className="space-y-1.5" data-testid="heatmap">
              {(data.vertical_heatmap || []).map((v) => (
                <div key={v.vertical} className="flex items-center justify-between gap-2 bg-white border border-slate-200 rounded p-2">
                  <div className="text-xs text-slate-900 capitalize flex-1">{v.vertical}</div>
                  <div className="text-[11px] font-mono text-slate-500">{fmtMoney(v.net_profit)}</div>
                  <div className={`text-[10px] font-mono px-1.5 py-0.5 rounded border uppercase ${verdictColor(v.verdict)}`}>{v.verdict}</div>
                </div>
              ))}
            </div>
          </div>
          {/* Vertical benchmark */}
          <div className="lg:col-span-3 bg-white border border-slate-200 rounded-md p-5 mt-2">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono mb-3">Vertical Benchmark</div>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]" data-testid="benchmark-table">
                <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-2 py-2">Vertical</th>
                    <th className="text-right px-2 py-2">Spend</th>
                    <th className="text-right px-2 py-2">Revenue</th>
                    <th className="text-right px-2 py-2">Profit</th>
                    <th className="text-right px-2 py-2">ROI</th>
                    <th className="text-right px-2 py-2">Avg CPC</th>
                    <th className="text-right px-2 py-2">Avg RPC</th>
                    <th className="text-right px-2 py-2">Camp.</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {bench.map((v) => (
                    <tr key={v.vertical} data-testid={`bench-${v.vertical}`} className="hover:bg-slate-100/30">
                      <td className="px-2 py-1.5 capitalize text-slate-900">{v.vertical}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-slate-600">${v.spend.toLocaleString()}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-slate-600">${v.revenue.toLocaleString()}</td>
                      <td className={`px-2 py-1.5 text-right font-mono font-medium ${v.net_profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>${v.net_profit.toLocaleString()}</td>
                      <td className={`px-2 py-1.5 text-right font-mono ${v.roi >= 0 ? "text-emerald-600" : "text-red-600"}`}>{v.roi.toFixed(1)}%</td>
                      <td className="px-2 py-1.5 text-right font-mono text-slate-500">${v.avg_cpc}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-slate-500">${v.avg_rpc}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-slate-500">{v.campaigns}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
